// Space identity — every shared space has its OWN netti, seeded from the space
// and tinted by the people standing in it: the seed key concatenates the slug
// with the sorted member ids, so the space's living color genuinely shifts when
// membership changes. Pure derivations over lib/organism/identity — the same
// pipeline that gives each person their organism.

import { organismFromSeed, seedFromString } from "../organism/identity";
import type { ColorState } from "../organism/color";

/** The stable identity string for a space — slug + the members currently in it. */
export function spaceKey(slug: string, memberIds: string[] = []): string {
  const members = [...memberIds].sort().join("+");
  return members ? `space:${slug}:${members}` : `space:${slug}`;
}

/** The space's harmony-locked palette (its netti's color). */
export function spaceColor(slug: string, memberIds: string[] = []): ColorState {
  return organismFromSeed(spaceKey(slug, memberIds));
}

/** The space's form seed (drives a MemoryOrganism / OrganismBackdrop body). */
export function spaceSeed(slug: string, memberIds: string[] = []): number {
  return seedFromString(spaceKey(slug, memberIds));
}

/** CSS colors for lightweight (non-WebGL) space marks — the ring center cell,
 *  hairline tints, QR ink. */
export function spaceCss(state: ColorState): { core: string; soft: string; deep: string } {
  const p = state.primary;
  const s = state.secondary;
  const h = Math.round(p.hue);
  const sh = Math.round(s.hue);
  return {
    core: `hsl(${h} ${Math.round(p.sat * 100)}% ${Math.round(p.light * 100)}%)`,
    soft: `hsl(${sh} ${Math.round(s.sat * 90)}% ${Math.round(Math.min(0.72, s.light + 0.14) * 100)}%)`,
    deep: `hsl(${h} ${Math.round(p.sat * 70)}% 24%)`,
  };
}
